import { SOC, type SocToken } from '@/styles/tokens'

/**
 * Map marks: runtime-drawn icons, keyed by a string that fully describes them.
 *
 * SHAPE IS THE PRIMARY CHANNEL AND COLOUR IS SECONDARY (the recon map rule):
 * square = building footprint, circle = person, diamond = hazard (police).
 * That keeps the map readable at a glance, for a colour-blind player, and
 * against the busiest patch of the basemap.
 *
 * A layer never ships a sprite. Its features carry an `icon` key built by
 * {@link markKey}; the first time Mapbox meets an unknown key it fires
 * `styleimagemissing`, and {@link drawMarkImage} rasterises it from the key
 * alone. Colours come only from the SocTheme tokens.
 */

export type MarkShape = 'sq' | 'dm' | 'ci' | 'frame-sq' | 'frame-dm'

export interface MarkSpec {
  shape: MarkShape
  /** Body size in CSS px (side of the square/diamond, diameter of the circle). */
  size: number
  fill: SocToken | null
  /** 0–255. */
  fillAlpha: number
  stroke: SocToken | null
  strokeWidth: number
  /** ✕ through the body (destroyed). */
  cross?: boolean
  /** Brass notch off the top-right corner (quests on offer). */
  notch?: boolean
  /** Warning ring around the body (challenged). */
  ring?: boolean
}

const MARK_PREFIX = 'wg'
const LABEL_PREFIX = 'wl'

export function markKey(s: MarkSpec): string {
  const deco = `${s.cross ? 'x' : ''}${s.notch ? 'n' : ''}${s.ring ? 'r' : ''}`
  return [MARK_PREFIX, s.shape, s.size, s.fill ?? '-', s.fillAlpha, s.stroke ?? '-', s.strokeWidth, deco || '-'].join(':')
}

function isToken(v: string): v is SocToken {
  return Object.prototype.hasOwnProperty.call(SOC, v)
}

export function parseMarkKey(key: string): MarkSpec | null {
  const parts = key.split(':')
  if (parts.length !== 8 || parts[0] !== MARK_PREFIX) return null
  const [, shape, size, fill, alpha, stroke, strokeWidth, deco] = parts
  if (!['sq', 'dm', 'ci', 'frame-sq', 'frame-dm'].includes(shape)) return null
  if (fill !== '-' && !isToken(fill)) return null
  if (stroke !== '-' && !isToken(stroke)) return null
  return {
    shape: shape as MarkShape,
    size: Number(size),
    fill: fill === '-' ? null : (fill as SocToken),
    fillAlpha: Number(alpha),
    stroke: stroke === '-' ? null : (stroke as SocToken),
    strokeWidth: Number(strokeWidth),
    cross: deco.includes('x'),
    notch: deco.includes('n'),
    ring: deco.includes('r'),
  }
}

export type LabelFont = 'serif' | 'title'

export function labelKey(text: string, color: SocToken, font: LabelFont = 'serif'): string {
  return [LABEL_PREFIX, color, font, text].join(':')
}

export function parseLabelKey(key: string): { text: string; color: SocToken; font: LabelFont } | null {
  const parts = key.split(':')
  if (parts.length < 4 || parts[0] !== LABEL_PREFIX) return null
  const [, color, font] = parts
  if (!isToken(color) || (font !== 'serif' && font !== 'title')) return null
  return { color, font, text: parts.slice(3).join(':') }
}

// ---------------------------------------------------------------------------
// Rasterising (browser only)
// ---------------------------------------------------------------------------

export interface RasterImage {
  width: number
  height: number
  data: Uint8ClampedArray
}

/** Parses `rgb(r, g, b)` / `rgba(r, g, b, a)` token values. */
export function tokenRgba(token: SocToken, alpha255?: number): string {
  const m = SOC[token].match(/rgba?\(([^)]+)\)/)
  if (!m) return SOC[token]
  const [r, g, b, a] = m[1].split(',').map((v) => Number(v.trim()))
  const alpha = alpha255 !== undefined ? alpha255 / 255 : Number.isFinite(a) ? a : 1
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const PAD = 5

function pathShape(ctx: CanvasRenderingContext2D, shape: MarkShape, cx: number, cy: number, size: number): void {
  const h = size / 2
  ctx.beginPath()
  switch (shape) {
    case 'sq':
    case 'frame-sq':
      ctx.rect(cx - h, cy - h, size, size)
      break
    case 'dm':
    case 'frame-dm':
      ctx.moveTo(cx, cy - h)
      ctx.lineTo(cx + h, cy)
      ctx.lineTo(cx, cy + h)
      ctx.lineTo(cx - h, cy)
      ctx.closePath()
      break
    case 'ci':
      ctx.arc(cx, cy, h, 0, Math.PI * 2)
      break
  }
}

/** Corner brackets: the selection reticle. Square for buildings, points for police. */
function drawFrame(ctx: CanvasRenderingContext2D, s: MarkSpec, cx: number, cy: number): void {
  const h = s.size / 2
  const arm = Math.max(3, s.size * 0.28)
  ctx.lineCap = 'square'
  const strokeAll = (color: string, width: number) => {
    ctx.strokeStyle = color
    ctx.lineWidth = width
    ctx.beginPath()
    if (s.shape === 'frame-sq') {
      for (const [sx, sy] of [
        [-1, -1],
        [1, -1],
        [1, 1],
        [-1, 1],
      ]) {
        const x = cx + sx * h
        const y = cy + sy * h
        ctx.moveTo(x, y - sy * arm)
        ctx.lineTo(x, y)
        ctx.lineTo(x - sx * arm, y)
      }
    } else {
      pathShape(ctx, 'frame-dm', cx, cy, s.size)
    }
    ctx.stroke()
  }
  strokeAll(tokenRgba('bg-deep', 220), s.strokeWidth + 2)
  strokeAll(tokenRgba(s.stroke ?? 'ink'), s.strokeWidth)
}

/** Draws a mark from its spec. Returns null when no 2D canvas is available. */
export function drawMarkImage(spec: MarkSpec, pixelRatio: number): RasterImage | null {
  const extent = spec.size + PAD * 2 + (spec.ring ? 8 : 0) + (spec.notch ? 4 : 0)
  const px = Math.ceil(extent * pixelRatio)
  const canvas = document.createElement('canvas')
  canvas.width = px
  canvas.height = px
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(pixelRatio, pixelRatio)
  const c = extent / 2

  if (spec.shape === 'frame-sq' || spec.shape === 'frame-dm') {
    drawFrame(ctx, spec, c, c)
  } else {
    if (spec.ring) {
      ctx.beginPath()
      ctx.arc(c, c, spec.size / 2 + 4.5, 0, Math.PI * 2)
      ctx.strokeStyle = tokenRgba('warning')
      ctx.lineWidth = 1.5
      ctx.stroke()
    }
    if (spec.fill) {
      pathShape(ctx, spec.shape, c, c, spec.size)
      ctx.fillStyle = tokenRgba(spec.fill, spec.fillAlpha)
      ctx.fill()
    }
    if (spec.stroke && spec.strokeWidth > 0) {
      pathShape(ctx, spec.shape, c, c, spec.size - spec.strokeWidth)
      ctx.strokeStyle = tokenRgba(spec.stroke)
      ctx.lineWidth = spec.strokeWidth
      ctx.stroke()
    }
    if (spec.cross) {
      const h = spec.size / 2 - 2
      ctx.beginPath()
      ctx.moveTo(c - h, c - h)
      ctx.lineTo(c + h, c + h)
      ctx.moveTo(c + h, c - h)
      ctx.lineTo(c - h, c + h)
      ctx.strokeStyle = tokenRgba(spec.stroke ?? 'danger')
      ctx.lineWidth = 1.5
      ctx.stroke()
    }
    if (spec.notch) {
      const h = spec.size / 2
      ctx.fillStyle = tokenRgba('outline')
      ctx.fillRect(c + h - 1, c - h - 4, 5, 5)
      ctx.fillStyle = tokenRgba('brass')
      ctx.fillRect(c + h - 0.5, c - h - 3.5, 4, 4)
    }
  }
  const img = ctx.getImageData(0, 0, px, px)
  return { width: px, height: px, data: img.data }
}

const LABEL_FONTS: Record<LabelFont, string> = {
  serif: '400 13px "DM Serif Display", Georgia, serif',
  title: '500 11px "Oswald", "Roboto Condensed", sans-serif',
}

/** A text label with a dark halo, drawn in the design system's own fonts. */
export function drawLabelImage(text: string, color: SocToken, font: LabelFont, pixelRatio: number): RasterImage | null {
  const probe = document.createElement('canvas').getContext('2d')
  if (!probe) return null
  const display = font === 'title' ? text.toUpperCase() : text
  probe.font = LABEL_FONTS[font]
  const w = Math.ceil(probe.measureText(display).width) + 8
  const h = font === 'title' ? 16 : 18
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(w * pixelRatio)
  canvas.height = Math.ceil(h * pixelRatio)
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.scale(pixelRatio, pixelRatio)
  ctx.font = LABEL_FONTS[font]
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = tokenRgba('bg-deep', 235)
  ctx.lineWidth = 3
  ctx.strokeText(display, w / 2, h / 2 + 0.5)
  ctx.fillStyle = tokenRgba(color)
  ctx.fillText(display, w / 2, h / 2 + 0.5)
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height)
  return { width: canvas.width, height: canvas.height, data: img.data }
}

/** Resolves any `wg:`/`wl:` key to an image; null for keys that are not ours. */
export function imageForKey(key: string, pixelRatio: number): RasterImage | null {
  const mark = parseMarkKey(key)
  if (mark) return drawMarkImage(mark, pixelRatio)
  const label = parseLabelKey(key)
  if (label) return drawLabelImage(label.text, label.color, label.font, pixelRatio)
  return null
}
